/**
 * Estado das peças da maquete.
 *
 * Regra do projeto: não espalhar `useState` por dezenas de meshes. A cena
 * recebe um mapa `id -> EstadoPeca` e cada peça apenas renderiza o estado
 * que recebeu. Esta máquina de estados é pura e testável.
 */

export type EstadoPeca =
  | 'normal'
  | 'destacada'
  | 'transparente'
  | 'aberta'
  | 'afastada'
  | 'removida'

export type AcaoPeca =
  | 'destacar'
  | 'transparencia'
  | 'ocultar'
  | 'afastar'
  | 'abrir'
  | 'reclinar'
  | 'remover'
  | 'restaurar'

export const ESTADO_INICIAL: EstadoPeca = 'normal'

export interface DescricaoAcao {
  acao: AcaoPeca
  rotulo: string
}

const ROTULOS_ACAO: Record<AcaoPeca, string> = {
  destacar: 'Destacar',
  transparencia: 'Tornar transparente',
  ocultar: 'Ocultar',
  afastar: 'Afastar / explodir',
  abrir: 'Abrir',
  reclinar: 'Reclinar',
  remover: 'Remover',
  restaurar: 'Restaurar',
}

export const rotuloAcao = (acao: AcaoPeca): string => ROTULOS_ACAO[acao]

/**
 * Aplica uma ação didática. Ações são alternâncias: repetir `destacar` em uma
 * peça já destacada devolve ela ao normal, o que evita a cena travar em um
 * estado que o aluno não sabe desfazer.
 *
 * `ocultar` e `remover` convergem para `removida`: visualmente são o mesmo
 * resultado e o conjunto de estados do plano é mantido sem inflar.
 */
export function aplicarAcao(estado: EstadoPeca, acao: AcaoPeca): EstadoPeca {
  switch (acao) {
    case 'restaurar':
      return 'normal'
    case 'destacar':
      return estado === 'destacada' ? 'normal' : 'destacada'
    case 'transparencia':
      return estado === 'transparente' ? 'normal' : 'transparente'
    // reclinar reusa o estado `aberta`: nos dois casos a peça gira em torno de
    // uma dobradiça, só muda o eixo
    case 'abrir':
    case 'reclinar':
      return estado === 'aberta' ? 'normal' : 'aberta'
    case 'afastar':
      return estado === 'afastada' ? 'normal' : 'afastada'
    case 'ocultar':
    case 'remover':
      return estado === 'removida' ? 'normal' : 'removida'
  }
}

export type MapaEstados = Readonly<Record<string, EstadoPeca>>

export function estadoDe(mapa: MapaEstados, id: string): EstadoPeca {
  return mapa[id] ?? ESTADO_INICIAL
}

export function comAcao(mapa: MapaEstados, id: string, acao: AcaoPeca): MapaEstados {
  const proximo = aplicarAcao(estadoDe(mapa, id), acao)
  if (proximo === ESTADO_INICIAL) {
    const copia = { ...mapa }
    delete copia[id]
    return copia
  }
  return { ...mapa, [id]: proximo }
}

/** Restaurar cena: volta tudo ao estado inicial (critério de aceite 17.17). */
export function restaurarTudo(): MapaEstados {
  return {}
}

export const cenaModificada = (mapa: MapaEstados): boolean => Object.keys(mapa).length > 0

/** A peça contribui para a silhueta, mas está escondida pelo aluno. */
export const pecaVisivel = (estado: EstadoPeca): boolean => estado !== 'removida'

export const pecaTranslucida = (estado: EstadoPeca): boolean => estado === 'transparente'
